import { useSyncExternalStore } from "react";
import { env } from "@/env";
import { useFeatureFlags } from "@/lib/backend/utils/hooks/use-feature-flags";
import { type Appearance, usePreferencesStore } from "@/lib/store/preferences";
import { type ResolvedAppearance } from "@/lib/theme/boot";

/**
 * Which appearance the app paints, and whether the employee is allowed to choose at all.
 *
 * Dark mode ships as a beta, so three answers have to be combined and there is exactly one place
 * that combines them — the same rule `useAppReworkFlags` holds, for the same reason: a second place
 * that decides is a second place that has to be found when the beta is turned off in a hurry.
 *
 * 1. **Off is light.** With the flag off this returns `light` no matter what is stored, and no switch
 *    is offered anywhere. An organization that never joined the beta, or an app that cannot reach
 *    the api, renders exactly what it renders today.
 * 2. **The employee's choice outlives the flag.** Turning the beta off does not clear the preference —
 *    it stops honouring it. Turn it back on and the employee is where they left off, which matters
 *    when a beta is paused for a week and resumed.
 * 3. **`system` means system, continuously.** Not "whatever the phone said when the app started": iOS
 *    flips at sunset and Android on a schedule, and an app that only notices on a cold start is the
 *    one still glowing white in a dark room.
 */

/** The flag the organization settings will carry once this leaves beta. */
const ORGANIZATION_FLAG = "darkModeEnabled";

const PREFERS_DARK = "(prefers-color-scheme: dark)";

/**
 * The flags endpoint is generated from the backend's OpenAPI spec, so this field does not exist in
 * the types until the backend ships it — and the beta has to be testable before then. Reading it
 * defensively costs one function and means the switch starts working the moment the column lands,
 * with no release on this side.
 */
function organizationOptedIn(flags: unknown): boolean {
  return (
    typeof flags === "object" &&
    flags !== null &&
    (flags as Record<string, unknown>)[ORGANIZATION_FLAG] === true
  );
}

function query(): MediaQueryList | undefined {
  return typeof window.matchMedia === "function" ? window.matchMedia(PREFERS_DARK) : undefined;
}

function subscribe(onChange: () => void): () => void {
  const media = query();

  if (!media) {
    return () => {};
  }

  // `MediaQueryList` only became an `EventTarget` in Safari 14. The app still runs in older iOS
  // WebViews, where the deprecated pair is the only pair there is.
  if (typeof media.addEventListener === "function") {
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }

  media.addListener(onChange);
  return () => media.removeListener(onChange);
}

function systemAppearance(): ResolvedAppearance {
  return query()?.matches ? "dark" : "light";
}

/** What the phone itself is set to, kept live. Rule 3. */
export function useSystemAppearance(): ResolvedAppearance {
  return useSyncExternalStore(subscribe, systemAppearance, () => "light" as const);
}

/** Whether this organization is in the beta. Rule 1. */
export function useDarkModeAvailable(): boolean {
  const { data } = useFeatureFlags();

  // The env override is how the beta is exercised on staging before the organization flag exists,
  // and how a brand build can be pinned to light while its screens are still being checked.
  return env.VITE_FEATURE_DARK_MODE || organizationOptedIn(data);
}

export type AppearanceControl = {
  /** What the employee chose. Survives the flag being switched off — rule 2. */
  preference: Appearance;
  /** What that resolves to right now, and what the app should actually paint. */
  resolved: ResolvedAppearance;
  /** What the phone is set to, for the "Systeem" option to describe itself honestly. */
  system: ResolvedAppearance;
  /** Whether to offer the choice at all. A control that quietly does nothing is worse than none. */
  available: boolean;
  setPreference: (appearance: Appearance) => void;
};

export function useAppearance(): AppearanceControl {
  const preference = usePreferencesStore((state) => state.appearance);
  const setPreference = usePreferencesStore((state) => state.setAppearance);
  const system = useSystemAppearance();
  const available = useDarkModeAvailable();

  const resolved: ResolvedAppearance = !available
    ? "light"
    : preference === "system"
      ? system
      : preference;

  return { preference, resolved, system, available, setPreference };
}
